import { lockCompositionJobs, type CompositionJobLocks, type CompositionMediaPolicyLocks } from './compositionMediaPolicy.js';
import { validateCompositionRoute } from './compositionRoute.js';
import { assertCompositionMediaReceipt } from './compositionFinalProof.js';
import { PlatformError, type DocTxOps } from '@djgg/core';
import { canonicalDigest, canonicalJson } from '@djgg/gameplay-kit';
import { validateAcceptedCompositionV1 } from '../spec/gameSpec.js';
import type { BuildJob, CreationVersion, VersionGates } from '../types.js';
import type { BuildClaimAuthority } from './buildJobs.js';

export interface CompositionPublicationInput {
  jobId: string;
  claim: BuildClaimAuthority;
  segment: number;
  traceUri: string;
  artifactBucket: string;
}

/** Lock order: build job, creation, version, asset policy. This lock must be
 * retained through the atomic version/active-pointer/job-completion write. */
export async function lockCompositionPublication(
  tx: DocTxOps, input: CompositionPublicationInput, creationId: string, version: number, now: number,
  hint: CreationVersion,
): Promise<CompositionJobLocks> {
  const locks = await lockCompositionJobs(tx,input.jobId,hint);
  const job = locks.job;
  if (!job || job.creationId !== creationId || job.version !== version || job.status !== 'claimed'
    || !input.claim.claimToken || job.workerId !== input.claim.workerId || job.claimToken !== input.claim.claimToken
    || job.leaseGeneration !== input.claim.leaseGeneration || (job.leaseExpiry ?? 0) <= now
    || !job.measuredExecution || now >= job.measuredExecution.deadlineAt) {
    throw new PlatformError('conflict', 'Composed publication lost its active claim or deadline');
  }
  const segment = job.measuredExecution.traceSegments.find(row => row.segment === input.segment);
  if (!segment || segment.claimGeneration !== input.claim.leaseGeneration || segment.status !== 'running') {
    throw new PlatformError('conflict', 'Composed publication trace claim changed');
  }
  return locks;
}

/** Exact committed proof for the published phase. Media requires its separate
 * retained final proof; structural evidence cannot certify a changed bundle. */
export async function assertCompositionPublicationProof(job: BuildJob, version: CreationVersion, gates: VersionGates,
  expectedJobId: string, tx?: DocTxOps, policies?: CompositionMediaPolicyLocks): Promise<void> {
  const run = job.measuredExecution!;
  await validateCompositionRoute(job, version, expectedJobId);
  const authority = version.executionPolicy?.composition
    ? await validateAcceptedCompositionV1(version.executionPolicy.composition) : undefined;
  const barrier = run.compositionBarrier;
  const proof = gates.compositionProof;
  const checkpoint = run.checkpoint;
  const retained = barrier?.structural?.candidate;
  if (authority?.media) {
    const final = run.compositionFinalProof;
    if (!tx || !barrier || !final || ![4, 5].includes(run.recipe.revision) || barrier.authorityDigest !== authority.digest
      || barrier.proofDigest !== await canonicalDigest(barrier.structural.candidate.report.compositionProof) || final.authorityDigest !== authority.digest || run.compositionDigest !== authority.digest
      || barrier.structural.candidateDigest !== await canonicalDigest(barrier.structural.candidate)
      || final.structuralProofDigest !== barrier.proofDigest || final.mediaBasisDigest !== barrier.mediaBasisDigest
      || final.candidateDigest !== await canonicalDigest(final.candidate)
      || gates.srcSha256 !== final.candidate.source.sha256
      || final.receiptDigest !== await canonicalDigest(final.receipt)
      || final.receiptDigest !== await canonicalDigest(final.candidate.report.compositionMedia)
      || checkpoint?.revision !== final.checkpointRevision || checkpoint.currentCandidateId !== final.candidateId
      || checkpoint.bestCandidateId !== final.candidateId
      || final.candidateDigest !== await canonicalDigest(checkpoint.candidates[final.candidateId] ?? null)
      || !proof || await canonicalDigest(proof) !== await canonicalDigest(final.candidate.report.compositionProof ?? null)
      // The version exposes exactly the strips its receipt deferred.
      || canonicalJson(gates.deferredMedia ?? null) !== canonicalJson(final.receipt.deferred ?? null)) {
      throw new PlatformError('conflict','Composed publication requires the exact retained final-media proof');
    }
    await assertCompositionMediaReceipt(tx,job,version,authority,final.receipt,expectedJobId,policies);
    return;
  }

  if (!authority || ![4, 5].includes(run.recipe.revision) || run.compositionDigest !== authority.digest || gates.deferredMedia !== undefined
    || !barrier || barrier.authorityDigest !== authority.digest || barrier.mediaJobIds.length !== 0
    || !proof || barrier.proofDigest !== await canonicalDigest(proof)
    || checkpoint?.revision !== barrier.checkpointRevision || checkpoint.bestCandidateId !== barrier.candidateId
    || checkpoint.currentCandidateId !== barrier.candidateId || !retained?.runnable || !retained.dist
    || retained.candidateId !== barrier.candidateId || gates.srcSha256 !== retained.source.sha256
    || barrier.structural.candidateDigest !== await canonicalDigest(retained)
    || barrier.structural.candidateDigest !== await canonicalDigest(checkpoint.candidates[barrier.candidateId] ?? null)
    || barrier.proofDigest !== await canonicalDigest(retained.report.compositionProof ?? null)) {
    throw new PlatformError('conflict', 'Composed publication requires the exact retained coded proof barrier');
  }
}
